import React, { ReactElement } from 'react'
// Commons imports.
import ProgressIndicator, { IndicatorVariant } from '../ProgressIndicator'
import Button from '../Button'
// Sub imports.
import { WizardStep, WizardStepAction } from './Types'
import { EMPTY_STRING } from '../../Constants/Separators'
// CSS imports. Commented out because of current build process.
// import './wizard.scss';

/**
 * Component properties type.
 */
type Props = {
    wizardSteps: WizardStep[]
    activeStepIndex: number
    /** When true, root and action buttons receive router admin classes for styling inside a `router-modal` shell. */
    isRouterMode?: boolean
}

/**
 * Wizard common reusable component.
 *
 * @param wizardSteps Array of wizard steps.
 * @param activeStepIndex Index of active step.
 */
const Wizard: React.FC<Props> = ({ wizardSteps, activeStepIndex, isRouterMode }: Props): ReactElement => {
    /**
     * Builds wizard step element.
     *
     * @param wizardStepItem Wizard step properties.
     * @param index Index of wizard step in list of all steps.
     */
    const toWizardStep = (wizardStepItem: WizardStep, index: number): ReactElement => {
        const { title, subTitle, content, actions } = wizardStepItem
        const wizardStepClass = `wizard-step ${index === activeStepIndex ? 'active-step' : 'hidden-step'}`
        const contentClass = `content ${index === activeStepIndex ? 'active-content' : 'hidden-content'}`
        const subTitleClass = `sub-title ${index === activeStepIndex ? 'active-subtitle' : 'hidden-subtitle'}`

        /**
         * Builds action element.
         *
         * @param action Action properties.
         * @param indexAction Action index in list of actions.
         */
        const toAction = (action: WizardStepAction, indexAction: number): ReactElement => {
            const { onClick, buttonText, isOutlined, isEnabled } = action
            const wizardButtonClass: string = [
                'wizard-button',
                isRouterMode === true ? 'wizard-button--router-mode' : EMPTY_STRING
            ]
                .filter(Boolean)
                .join(' ')

            return (
                <Button
                    key={`${buttonText}${indexAction}`}
                    customClass={wizardButtonClass}
                    onClick={onClick}
                    label={buttonText}
                    isOutlined={isOutlined}
                    isDisabled={!isEnabled}
                />
            )
        }

        return (
            <div className={wizardStepClass} key={`${title}${subTitle}${index}`}>
                <div className='title'>{title}</div>
                <div className='subtitle-animation-wrapper'>
                    <div className={subTitleClass}>{subTitle}</div>
                </div>
                <div className={contentClass}>{content}</div>
                <div className='actions'>{actions.map(toAction)}</div>
                <div className='dots'>
                    <ProgressIndicator
                        length={wizardSteps.length}
                        activeIndex={activeStepIndex}
                        variant={IndicatorVariant.DOTS}
                    />
                </div>
            </div>
        )
    }

    const wizardRootClass: string = ['wizard', isRouterMode === true ? 'wizard--router-mode' : EMPTY_STRING]
        .filter(Boolean)
        .join(' ')

    return <div className={wizardRootClass}>{wizardSteps.map(toWizardStep)}</div>
}

export default Wizard
